import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import {
    Box,
    Typography,
    Button,
    TextField,
    Toolbar,
    Grid,
    Card,
    IconButton,
    Tooltip,
    Modal,
    Chip,
    CircularProgress,
    InputAdornment,
    Pagination,
    Select,
    MenuItem,
    Menu
} from '@mui/material';
import {
    Search as SearchIcon,
    AutoAwesome as SparklesIcon,
    Newspaper as NewspaperIcon,
    OpenInNew as ExternalLinkIcon,
    CalendarToday as CalendarIcon,
    Person as UserIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    Publish as PublishIcon,
    CloudUpload as UploadIcon,
    LightbulbOutlined as LightbulbOutlinedIcon,
    ChevronRight as ChevronRightIcon,
    PlayCircleOutline as PlayCircleOutlineIcon,
    Add as AddIcon,
    Tune as TuneIcon,
    Facebook as FacebookIcon,
    LibraryBooks as BlogIcon,
    Save as SaveIcon,
    Search,
    ArrowBack
} from '@mui/icons-material';

import RichTextEditor from '../../common/HtmlEditor';
import DOMPurify from 'dompurify';

import styles from './styles';
import { messageShow } from '../../common/api/actions';
import { getAiNews, generateBlogFromNews, uploadThumbnail } from '../api/actions/aiActions';
import { getCms, getPreferredTopics, getBaseUrls } from '../api/actions/query';
import { createOrUpdateCms } from '../api/actions/mutation';
import { checkPermission } from "../../common/Sidebar/checkPermission";
import BrandedLoading from "../../common/BrandedLoading";
import { createEvent, sendFbPost, sendNewsletterCampaign } from '../../marketing/api/action/mutation';
import routes from '../api/routes';


class AiBlog extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchTerm: '',
            news: [],
            isLoading: false,
            generatingLink: null,
            showGenConfig: false,
            tagInput: '',
            isPublishing: false,
            isUploading: false,
            currentDraft: null,
            isPreviewOpen: false,
            isEditing: false,
            seoKeywordInput: '',
            targetBaseUrl: '',
            availableBaseUrls: [],
            editedDraft: {
                title: '',
                content: '',
                tags: [],
                thumbnail: '',
                metaDescription: '',
                metaKeywords: [],
                subtitle: '',
                thumbnailUrl: '',
                authorName: '',
                focusKeywords: '',
                slug: '',
                sort: 0,
                targetBaseUrl: ''
            },
            genConfig: {
                tone: 'Professional',
                length: 'Medium',
                language: 'English'
            },
            filter: 'All',
            permissions: {
                canView: false,
                canCreate: false,
                canEdit: false,
                canDelete: false
            },
            isVerifying: true,
            page: 1,
            rowsPerPage: 20,
            topics: [],
            topicsLoaded: false,
            isSharing: false,
            searchKeywords: [],
            newKeyword: '',
            isSavingKeyword: false,
            keywordsDocId: null,
            isAddingKeyword: false,
            category: 'blog',
            manualUrl: '',
            isGeneratingManual: false,
            keywordSearchTerm: '',
            keywordMenuAnchor: null
        };
        this.editorRef = React.createRef();
    }

    componentDidMount() {
        this.verifyPermissions();
        this.loadTopics();
        this.loadSearchKeywords();
        void this.loadBaseUrls();

        // Parse category from query param if it exists (e.g. ?cat=news)
        const urlParams = new URLSearchParams(this.props.location.search);
        const catParam = urlParams.get('cat');
        if (catParam) {
            this.setState({ category: catParam });
        }
    }

    loadBaseUrls = async () => {
        try {
            const { getBaseUrls } = this.props;
            if (getBaseUrls) {
                const response = await getBaseUrls();
                if (response?.data?.success) {
                    const list = response.data.data || [];
                    this.setState({ availableBaseUrls: list });
                }
            }
        } catch (error) {
            console.error("Error fetching base URLs in AiBlog:", error);
        }
    };

    loadSearchKeywords = async () => {
        const { getCms } = this.props;
        try {
            const response = await getCms();
            if (response.data.success) {
                const doc = response.data.data.find(item => item.tag === 'SEARCH_KEYWORDS');
                if (doc) {
                    this.setState({ 
                        searchKeywords: doc.keywords || [], 
                        keywordsDocId: doc._id 
                    });
                }
            }
        } catch (error) {
            console.error("Error loading search keywords:", error);
        }
    };

    handleSaveKeyword = async (isDelete = false, keywordToDelete = null) => {
        const { newKeyword, searchKeywords, keywordsDocId } = this.state;
        const { createOrUpdateCms, messageShow } = this.props;

        let updatedKeywords = [...searchKeywords];
        
        if (isDelete) {
            updatedKeywords = updatedKeywords.filter(k => k !== keywordToDelete);
        } else {
            const val = newKeyword.trim();
            if (!val) return;
            if (val.length > 20) {
                messageShow("Keyword cannot exceed 20 characters.");
                return;
            }
            if (searchKeywords.includes(val)) {
                messageShow("Keyword already exists.");
                return;
            }
            if (searchKeywords.length >= 20) {
                messageShow("Maximum limit of 20 saved keywords reached. Please delete some before adding more.");
                return;
            }
            updatedKeywords.push(val);
        }

        this.setState({ isSavingKeyword: true });
        try {
            const cmsData = {
                _id: keywordsDocId,
                title: "SAVED_SEARCH_KEYWORDS",
                tag: "SEARCH_KEYWORDS",
                keywords: updatedKeywords,
                description: "User saved search keywords for AI Blog",
                category: this.state.category || 'blog'
            };
            const response = await createOrUpdateCms(cmsData);
            if (response.data.success) {
                this.setState({
                    searchKeywords: updatedKeywords,
                    newKeyword: '',
                    isAddingKeyword: false,
                    keywordsDocId: response.data.data._id || keywordsDocId
                });
                messageShow(isDelete ? "Keyword removed!" : "Keyword saved!");
            }
        } catch (error) {
            console.error("Error saving keyword:", error);
            messageShow("Error saving keyword.");
        } finally {
            this.setState({ isSavingKeyword: false });
        }
    };

    loadTopics = async () => {
        const { getPreferredTopics } = this.props;
        try {
            const response = await getPreferredTopics();
            const topics = response.data?.data || [];
            this.setState({ topics, topicsLoaded: true });
        } catch (e) {
            this.setState({ topics: [], topicsLoaded: true });
        }
    };

    verifyPermissions = async () => {
        const modulePermissions = await checkPermission("marketing", "resource");
        this.setState({
            permissions: {
                canView: modulePermissions.canView,
                canCreate: modulePermissions.canCreate,
                canEdit: modulePermissions.canEdit,
                canDelete: modulePermissions.canDelete
            },
            isVerifying: false
        }, () => {
            if (this.state.permissions.canView) {
                const { topics } = this.state;
                const randomTopic = topics.length
                    ? topics[Math.floor(Math.random() * topics.length)]
                    : '';
                if (randomTopic) {
                    this.setState({ searchTerm: randomTopic }, () => this.handleSearch());
                }
            }
        });
    }

    handleSearch = async (e) => {
        if (e) e.preventDefault();
        const { searchTerm } = this.state;
        const { getAiNews, messageShow } = this.props;

        if (!searchTerm.trim()) return;

        const trimmedTerm = searchTerm.trim();
        const isExplicitUrl = trimmedTerm.startsWith('http://') || trimmedTerm.startsWith('https://') || trimmedTerm.startsWith('www.');
        const isPathUrl = /^([\w\-]+\.)+[a-z]{2,}\/\S+$/i.test(trimmedTerm) && !/\s/.test(trimmedTerm);

        // If the user pasted a URL, directly generate a blog from it
        if (isExplicitUrl || isPathUrl) {
            let link = trimmedTerm;
            if (!link.startsWith('http://') && !link.startsWith('https://')) {
                link = 'https://' + link;
            }
            this.handleGenerateBlog({ link: link, source: "Manual URL", title: "Generated from URL" });
            return;
        }

        this.setState({ isLoading: true });
        try {
            const response = await getAiNews(searchTerm);
            if (response.data.success) {
                this.setState({ news: response.data.items });
            } else {
                messageShow(response.data.error || response.data.message || "Failed to fetch news.");
            }
        } catch (error) {
            console.error("Error fetching news:", error);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "Error fetching news.";
            messageShow(errorMsg);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handleGenerateBlog = async (item) => {
        const { generateBlogFromNews, messageShow } = this.props;
        this.setState({ generatingLink: item.link });
        try {
            const response = await generateBlogFromNews(item);
            if (response.data.success) {
                const generatedData = response.data.blog || {};
                let articleBodyHtml = generatedData.content || '';
                if (!articleBodyHtml && (generatedData.introduction || generatedData.sections)) {
                    let compiled = '';
                    if (generatedData.introduction) {
                        compiled += `<p>${generatedData.introduction}</p>`;
                    }
                    if (Array.isArray(generatedData.sections)) {
                        generatedData.sections.forEach(sec => {
                            if (sec.heading) compiled += `<h2>${sec.heading}</h2>`;
                            if (sec.content) compiled += `<p>${sec.content}</p>`;
                        });
                    }
                    if (generatedData.conclusion_and_cta) {
                        compiled += `<h2>Conclusion</h2><p>${generatedData.conclusion_and_cta}</p>`;
                    }
                    articleBodyHtml = compiled;
                }

                const sourceFooter = `<br/><hr/><p><i>Source: ${item.source}</i><br/><i>URL: <a href="${item.link}" target="_blank" rel="noopener noreferrer">View Source</a></i></p>`;
                const finalContent = articleBodyHtml + sourceFooter;

                // Helper to normalize array or string into clean string array
                const parseToArray = (val) => {
                    if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
                    if (typeof val === 'string' && val.trim()) return val.split(',').map(v => v.trim()).filter(Boolean);
                    return [];
                };

                const parsedTags = parseToArray(generatedData.tags || generatedData.seo_meta_tags || generatedData.seoKeywords || generatedData.seo_keywords);
                const parsedKeywords = parseToArray(generatedData.seoKeywords || generatedData.seo_keywords || generatedData.seo_meta_tags || generatedData.tags);
                const parsedDescription = generatedData.seoDescription || generatedData.seo_description || generatedData.meta_description || generatedData.description || '';

                this.setState((prevState) => ({
                    currentDraft: generatedData,
                    editedDraft: {
                        title: generatedData.title || generatedData.seoTitle || item.title || '',
                        content: finalContent,
                        tags: parsedTags,
                        thumbnail: '',
                        metaDescription: parsedDescription,
                        metaKeywords: parsedKeywords,
                        subtitle: generatedData.subtitle || '',
                        thumbnailUrl: '',
                        authorName: generatedData.author_name || generatedData.authorName || '',
                        slug: (generatedData.title || generatedData.seoTitle || item.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
                        category: (prevState.category === 'news' ? 'blog' : prevState.category),
                        type: 'blog',
                        targetBaseUrl: prevState.targetBaseUrl || ""
                    },
                    isPreviewOpen: true,
                    isEditing: false
                }));
                messageShow(`Blog draft generated successfully!`);
            } else {
                messageShow(response.data.error || response.data.message || `Failed to generate blog.`);
            }
        } catch (error) {
            console.error("Error generating content:", error);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "Error generating blog";
            messageShow(errorMsg);
        } finally {
            this.setState({ generatingLink: null });
        }
    };

    handlePublish = async (isDraft = false) => {
        const { editedDraft } = this.state;
        const { createOrUpdateCms, messageShow, history } = this.props;

        if (!editedDraft.title.trim() || !editedDraft.content.trim()) {
            messageShow("Title and content are required.");
            return;
        }

        let finalContentToPublish = editedDraft.content;

        if (editedDraft.tags && editedDraft.tags.length > 3) {
            messageShow("Maximum 3 tags allowed per blog!");
            return;
        }

        const category = editedDraft.category || this.state.category || 'blog';
        
        if (category === 'newsletter' && !isDraft) {
            const confirmPublish = window.confirm("This will publish the newsletter to your website AND send it to all subscribers. Do you want to proceed?");
            if (!confirmPublish) return;
        }

        const finalSlug = (editedDraft.slug || editedDraft.title).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

        const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch(e) { return {}; } })();
        const userId = currentUser._id || '';

        this.setState({ isPublishing: true });
        try {
            // Combine tags and thumbnail info if needed, or just publish
            const resolvedTargetBaseUrl = typeof editedDraft.targetBaseUrl === 'string'
                ? editedDraft.targetBaseUrl
                : (typeof this.state.targetBaseUrl === 'string' ? this.state.targetBaseUrl : "");

            const cmsData = {
                title: editedDraft.title,
                category: editedDraft.category || this.state.category || 'blog',
                type: editedDraft.type || 'blog',
                targetBaseUrl: resolvedTargetBaseUrl,
                tag: (editedDraft.tags || []).map(t => t.trim().replace(/\s+/g, '_').toUpperCase()).join(','),
                content: finalContentToPublish,
                thumbnail: editedDraft.thumbnail,
                description: editedDraft.tags.join(', '),
                slug: finalSlug,
                authorId: userId,
                isAiGenerated: true,
                createdById: userId,
                searchTags: editedDraft.tags || [],
                status: isDraft ? 'draft' : 'published',
                sort: editedDraft.sort || 0,
                subtitle: editedDraft.subtitle,
                mediaType: editedDraft.mediaType || 'Image',
                seo: {
                    authorName: (editedDraft.authorName || '').trim(),
                    metaKeywords: editedDraft.metaKeywords || [],
                    metaDescription: editedDraft.metaDescription || '',
                    metaTitle: editedDraft.title,
                    ogTitle: editedDraft.title,
                    ogDescription: editedDraft.metaDescription || '',
                    ogImage: editedDraft.thumbnail || '',
                    targetBaseUrl: resolvedTargetBaseUrl,
                    route: `/${(editedDraft.category || this.state.category || 'blog').toLowerCase()}/${finalSlug}`
                },
                isSent: ( (editedDraft.category || this.state.category || '').toLowerCase() === 'newsletter' && !isDraft) ? true : false
            };

            const response = await createOrUpdateCms(cmsData);
            if (response.data.success) {
                // If it's a newsletter and we're publishing (not draft), send to subscribers too
                if ((cmsData.category || '').toLowerCase() === 'newsletter' && !isDraft) {
                    try {
                        const campResult = await sendNewsletterCampaign(response.data.data._id);
                        if (campResult && campResult.error) throw new Error(campResult.error);
                        messageShow("Newsletter published to site and sent to subscribers!");
                    } catch (err) {
                        console.error("sendNewsletterCampaign failed:", err);
                        messageShow("Newsletter published to site, but failed to send to subscribers.");
                    }
                } else {
                    messageShow(isDraft ? "Blog saved as draft!" : "Blog published successfully!");
                }
                
                this.setState({ isPreviewOpen: false });
                if (this.props.onClose) {
                    this.props.onClose();
                } else {
                    history.push(routes.cmsList.path);
                }
            } else {
                messageShow(response.data.message || "Failed to publish.");
            }
        } catch (error) {
            console.error("Error publishing blog:", error);
            messageShow("Error publishing blog.");
        } finally {
            this.setState({ isPublishing: false });
        }
    };


    
    handlePostToFacebook = async () => {
        const { editedDraft } = this.state;
        const { createEvent, sendFbPost, messageShow } = this.props;

        if (!editedDraft.title.trim() || !editedDraft.content.trim()) {
            messageShow("Title and content are required.");
            return;
        }

        this.setState({ isSharing: true });
        try {
            // 1. Create the Facebook post event
            const eventPayload = {
                name: `Ai Blog: ${editedDraft.title}`,
                message: `${editedDraft.title}\n\nRead more at: ${window.location.host}/blog/${editedDraft.slug || ''}`,
                notificationType: 'fb-post',
                date: new Date().toISOString(),
                eventImg: editedDraft.thumbnail || ''
            };

            const createResponse = await createEvent(eventPayload);
            
            const postData = createResponse.post || createResponse.data?.post || createResponse.data;
            
            if (postData && postData._id) {
                // 2. Send the created post to Facebook
                const sendResponse = await sendFbPost(postData._id);
                
                if (sendResponse && (sendResponse.message === "Completed" || !sendResponse.error)) {
                    messageShow("Successfully posted to Facebook!");
                } else {
                    messageShow(sendResponse.error || "Failed to post to Facebook.");
                }
            } else {
                messageShow(createResponse.error || "Failed to prepare Facebook post.");
            }
        } catch (error) {
            console.error("Error posting to Facebook:", error);
            messageShow("An error occurred while posting to Facebook.");
        } finally {
            this.setState({ isSharing: false });
        }
    };

    handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const { uploadThumbnail, messageShow } = this.props;
        this.setState({ isUploading: true });

        try {
            const response = await uploadThumbnail(file);
            // In API result check for file property or url
            const imageUrl = response.data.file || response.data.url;
            if (imageUrl) {
                this.setState(prevState => ({
                    editedDraft: { ...prevState.editedDraft, thumbnail: imageUrl }
                }));
                messageShow("Thumbnail uploaded successfully!");
            }
        } catch (error) {
            console.error("Upload failed:", error);
            messageShow("Upload failed.");
        } finally {
            this.setState({ isUploading: false });
        }
    };

    renderHelper = () => {
        const { classes } = this.props;
        return (
            <Box sx={{ mt: 5 }}>
                <Box className={classes.tipsContainer}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} sx={{ color: '#0d3559' }}>
                        <Box display="flex" alignItems="center">
                            <LightbulbOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                SMART BLOG GENERATOR QUICK TIPS
                            </Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <Box display="flex" alignItems="flex-start">
                                <ChevronRightIcon sx={{ color: '#111827', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#4B5563', lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 600, color: '#1F2937' }}>Trend Discovery:</span> Search for industry topics to find the latest news articles from around the web.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Box display="flex" alignItems="flex-start">
                                <ChevronRightIcon sx={{ color: '#111827', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#4B5563', lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 600, color: '#1F2937' }}>AI Generation:</span> Click the sparkle icon to transform any news story into a full SEO-optimized blog draft.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Box display="flex" alignItems="flex-start">
                                <ChevronRightIcon sx={{ color: '#111827', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#4B5563', lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 600, color: '#1F2937' }}>Smart Editing:</span> Review and refine your draft. Add your brand's unique voice and customize SEO tags.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <Box display="flex" alignItems="flex-start">
                                <ChevronRightIcon sx={{ color: '#111827', fontSize: 20, mt: '2px', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ color: '#4B5563', lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 600, color: '#1F2937' }}>One-Click Publish:</span> Instantly push your finalized blog to your CMS and reach your audience.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        );
    }

    render() {
        const { classes } = this.props;
        const {
            searchTerm,
            news,
            isLoading,
            generatingLink,
            isPreviewOpen,
            isEditing,
            editedDraft,
            isPublishing,
            isUploading,
            permissions,
            isVerifying,
            page,
            rowsPerPage
        } = this.state;

        const filteredNews = news.filter(item => {
            if (this.state.filter === 'All') return true;
            if (this.state.filter === 'News' && item.source.toLowerCase().includes('news')) return true;
            if (this.state.filter === 'Tech' && (item.source.toLowerCase().includes('tech') || item.source.toLowerCase().includes('the verge'))) return true;
            if (this.state.filter === 'AI' && (item.title.toLowerCase().includes('ai') || item.description.toLowerCase().includes('ai'))) return true;
            return true;
        });

        const startIndex = (page - 1) * rowsPerPage;
        const selectedNews = filteredNews.slice(startIndex, startIndex + rowsPerPage);
        const totalPages = Math.ceil(filteredNews.length / rowsPerPage);

        if (isVerifying) {
            return <BrandedLoading />;
        }



        const isModal = this.props.isModal;

        return (
            <Box sx={isModal ? { p: 0 } : {}}>
                {!isModal && (
                    <Toolbar sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)', mb: 1, minHeight: '48px !important' }}>
                        <IconButton
                            className={classes.menuButton}
                            color="inherit"
                            onClick={() => this.props.history.push(routes.cmsList.path)}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h6" color="inherit" className={classes.grow}>
                            Resource Manager
                        </Typography>
                        <Tooltip title="Add Resource Manually" arrow>
                            <IconButton
                                onClick={() => this.props.history.push(routes.cmsCreate.path)}
                                disabled={!permissions.canCreate}
                                sx={{
                                    borderRadius: '12px',
                                    color: '#0d3559',
                                    '&:hover': {
                                        bgcolor: 'rgba(13, 53, 89, 0.08)',
                                        transform: 'translateY(-2px)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <AddIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                        </Tooltip>
                    </Toolbar>
                )}

                <Box className={isModal ? "" : classes.wrapper} sx={{ pt: isModal ? 0 : 1 }}>
                    <Box sx={{ 
                        mb: 2, 
                        p: 1.5, 
                        background: 'linear-gradient(135deg, #ffffff 0%, #f9fbff 100%)', 
                        borderRadius: '12px', 
                        boxShadow: '0 10px 40px rgba(13, 53, 89, 0.05)',
                        border: '1px solid rgba(233, 236, 239, 0.5)',
                        display: 'flex',
                        flexDirection: { xs: 'column', lg: 'row' },
                        alignItems: { xs: 'stretch', lg: 'center' },
                        gap: 2
                    }}>
                        <form onSubmit={this.handleSearch} style={{ display: 'flex', gap: '12px', flexGrow: 1, minWidth: '300px' }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Enter a topic to research, or paste an article URL..."
                                value={searchTerm}
                                onChange={(e) => this.setState({ searchTerm: e.target.value })}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: '#0d3559', opacity: 0.5 }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        borderRadius: '8px',
                                        height: '40px',
                                        bgcolor: 'white',
                                        fontSize: '0.875rem',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                                        '& fieldset': { borderColor: '#E9ECEF' },
                                        '&:hover fieldset': { borderColor: '#111827 !important' },
                                        '& .MuiInputBase-input': { padding: '8.5px 14px' }
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                type="submit"
                                disabled={isLoading || this.state.generatingLink === searchTerm || !permissions.canCreate}
                                startIcon={(isLoading || this.state.generatingLink === searchTerm) ? <CircularProgress size={16} color="inherit" /> : <Search sx={{ fontSize: 18 }} />}
                                sx={{ 
                                    textTransform: 'none', 
                                    fontWeight: 600, 
                                    px: 3,
                                    py: 1, 
                                    borderRadius: '8px',
                                    height: '40px',
                                    whiteSpace: 'nowrap',
                                    background: 'linear-gradient(135deg, #0d3559 0%, #1a4f7c 100%)',
                                    color: 'white',
                                    boxShadow: '0 4px 15px rgba(13, 53, 89, 0.25)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #0a2b48 0%, #154269 100%)',
                                        boxShadow: '0 6px 20px rgba(13, 53, 89, 0.35)',
                                        color: 'white'
                                    },
                                    '&.Mui-disabled': {
                                        background: 'linear-gradient(135deg, #0d3559 0%, #1a4f7c 100%)',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        opacity: 0.8,
                                        boxShadow: 'none'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {this.state.generatingLink === searchTerm ? 'Generating...' : (isLoading ? 'Analyzing...' : 'Search / Generate')}
                            </Button>
                        </form>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                {this.state.searchKeywords.slice(0, 3).map((kw, idx) => (
                                    <Chip
                                        key={idx}
                                        label={kw}
                                        onClick={() => this.setState({ searchTerm: kw }, () => this.handleSearch())}
                                        onDelete={() => this.handleSaveKeyword(true, kw)}
                                        sx={{ 
                                            bgcolor: 'rgba(13, 53, 89, 0.04)', 
                                            border: '1px solid rgba(13, 53, 89, 0.08)',
                                            fontWeight: 600,
                                            borderRadius: '8px',
                                            height: '32px',
                                            '&:hover': { 
                                                bgcolor: 'rgba(13, 53, 89, 0.08)', 
                                                borderColor: '#0d3559',
                                                color: '#0d3559' 
                                            },
                                            '& .MuiChip-deleteIcon': {
                                                fontSize: '16px',
                                                color: 'rgba(13, 53, 89, 0.3)',
                                                '&:hover': { color: '#ff4d4d' }
                                            }
                                        }}
                                    />
                                ))}

                                {this.state.searchKeywords.length > 3 && (
                                    <>
                                        <Chip
                                            label={`+${this.state.searchKeywords.length - 3} more`}
                                            onClick={(e) => this.setState({ keywordMenuAnchor: e.currentTarget })}
                                            sx={{
                                                bgcolor: 'rgba(13, 53, 89, 0.04)',
                                                border: '1px solid rgba(13, 53, 89, 0.08)',
                                                fontWeight: 600,
                                                borderRadius: '8px',
                                                height: '32px',
                                                color: '#0d3559',
                                                '&:hover': { bgcolor: 'rgba(13, 53, 89, 0.08)', borderColor: '#0d3559' },
                                                cursor: 'pointer'
                                            }}
                                        />
                                        <Menu
                                            anchorEl={this.state.keywordMenuAnchor}
                                            open={Boolean(this.state.keywordMenuAnchor)}
                                            onClose={() => this.setState({ keywordMenuAnchor: null })}
                                            PaperProps={{
                                                sx: { mt: 1, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', maxHeight: 250 }
                                            }}
                                        >
                                            <Box sx={{ p: 1, position: 'sticky', top: 0, bgcolor: 'white', zIndex: 2, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Filter keywords..."
                                                    value={this.state.keywordSearchTerm}
                                                    onChange={(e) => this.setState({ keywordSearchTerm: e.target.value })}
                                                    InputProps={{
                                                        startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: '#9CA3AF' }} />,
                                                        sx: { fontSize: '0.85rem', borderRadius: '8px', height: '32px' }
                                                    }}
                                                />
                                            </Box>
                                            {this.state.searchKeywords.slice(3)
                                                .filter(kw => kw.toLowerCase().includes(this.state.keywordSearchTerm.toLowerCase()))
                                                .map((kw, idx) => (
                                                <MenuItem key={idx} sx={{ display: 'flex', justifyContent: 'space-between', width: '250px' }}>
                                                    <Box 
                                                        onClick={() => {
                                                            this.setState({ searchTerm: kw, keywordMenuAnchor: null }, () => this.handleSearch());
                                                        }} 
                                                        sx={{ flexGrow: 1, textOverflow: 'ellipsis', overflow: 'hidden' }}
                                                    >
                                                        {kw}
                                                    </Box>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => { 
                                                            e.stopPropagation(); 
                                                            this.handleSaveKeyword(true, kw); 
                                                        }}
                                                        sx={{ color: 'rgba(13, 53, 89, 0.3)', '&:hover': { color: '#ff4d4d' } }}
                                                    >
                                                        <CloseIcon fontSize="small" />
                                                    </IconButton>
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </>
                                )}

                                {this.state.isAddingKeyword ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            autoFocus
                                            placeholder="Enter keyword..."
                                            value={this.state.newKeyword}
                                            onChange={(e) => this.setState({ newKeyword: e.target.value })}
                                            onKeyPress={(e) => e.key === 'Enter' && this.handleSaveKeyword()}
                                            inputProps={{ maxLength: 20 }}
                                            sx={{ 
                                                width: 150,
                                                '& .MuiOutlinedInput-root': {
                                                    height: '32px',
                                                    fontSize: '0.8rem',
                                                    borderRadius: '8px',
                                                    bgcolor: 'white'
                                                }
                                            }}
                                        />
                                        <IconButton size="small" onClick={() => this.handleSaveKeyword()} sx={{ color: '#0d3559' }}>
                                            {this.state.isSavingKeyword ? <CircularProgress size={16} /> : <AddIcon sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                        <IconButton size="small" onClick={() => this.setState({ isAddingKeyword: false, newKeyword: '' })}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                ) : (
                                    <Tooltip title="Add Recurring Keyword">
                                        <IconButton
                                            size="small"
                                            onClick={() => this.setState({ isAddingKeyword: true })}
                                            sx={{
                                                bgcolor: 'rgba(13, 53, 89, 0.04)',
                                                border: '1px dashed rgba(13, 53, 89, 0.2)',
                                                borderRadius: '8px',
                                                height: '32px',
                                                width: '32px',
                                                color: '#0d3559',
                                                '&:hover': { 
                                                    bgcolor: 'rgba(13, 53, 89, 0.08)',
                                                    borderColor: '#0d3559'
                                                }
                                            }}
                                        >
                                            <AddIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>


                    <Grid container spacing={3}>
                        {isLoading ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <Grid item xs={12} key={i}>
                                    <Card sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, p: 3, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 3 }}>
                                        <Box sx={{ width: 48, height: 48, bgcolor: 'action.hover', borderRadius: 2, flexShrink: 0 }} />
                                        <Box sx={{ flexGrow: 1, width: '100%' }}>
                                            <Box sx={{ height: 24, width: '70%', bgcolor: 'action.hover', mb: 1.5, borderRadius: 1 }} />
                                            <Box sx={{ height: 16, width: '100%', bgcolor: 'action.hover', mb: 1, borderRadius: 1 }} />
                                            <Box sx={{ height: 16, width: '40%', bgcolor: 'action.hover', borderRadius: 1 }} />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, ml: { xs: 0, sm: 'auto' } }}>
                                            <Box sx={{ width: 40, height: 40, bgcolor: 'action.hover', borderRadius: '50%' }} />
                                            <Box sx={{ width: 40, height: 40, bgcolor: 'action.hover', borderRadius: '50%' }} />
                                        </Box>
                                    </Card>
                                </Grid>
                            ))
                        ) : filteredNews.length > 0 ? (
                            <Box sx={{ width: '100%', mt: 2 }}>
                                {/* Tabular Header */}
                                <Box sx={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'minmax(120px, 140px) minmax(130px, 160px) 1fr minmax(100px, 120px)',
                                    gap: 2, 
                                    px: 3, 
                                    py: 1.5, 
                                    bgcolor: 'rgba(13, 53, 89, 0.05)', 
                                    borderRadius: '12px 12px 0 0',
                                    border: '1px solid rgba(233, 236, 239, 0.8)',
                                    borderBottom: 'none'
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Source</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Title</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</Typography>
                                </Box>

                                {selectedNews.map((item, index) => (
                                    <Box key={index} sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'minmax(120px, 140px) minmax(130px, 160px) 1fr minmax(100px, 120px)',
                                        gap: 2, 
                                        px: 3, 
                                        py: 2, 
                                        alignItems: 'center',
                                        bgcolor: '#ffffff',
                                        border: '1px solid rgba(233, 236, 239, 0.5)',
                                        borderTop: 'none',
                                        '&:last-child': { borderRadius: '0 0 12px 12px' },
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#F8F9FE',
                                            '& .action-btns': { opacity: 1 }
                                        }
                                    }}>
                                        <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.85rem' }}>
                                            {new Date(item.pubDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                        </Typography>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <NewspaperIcon sx={{ fontSize: 16, color: '#0d3559', opacity: 0.6 }} />
                                            <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 700, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.source}
                                            </Typography>
                                        </Box>

                                        <Typography variant="body2" sx={{ 
                                            fontWeight: 600, 
                                            color: '#1F2937', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.9rem'
                                        }}>
                                            {item.title}
                                        </Typography>
                                        
                                         <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                            <Tooltip title="AI Blog Generator" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => this.handleGenerateBlog(item)}
                                                    disabled={generatingLink !== null || !permissions.canCreate}
                                                    sx={{ 
                                                        bgcolor: 'rgba(13, 53, 89, 0.05)', 
                                                        color: '#0d3559',
                                                        '&:hover': { bgcolor: '#0d3559', color: 'white' },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    {generatingLink === item.link ? <CircularProgress size={16} color="inherit" /> : <BlogIcon sx={{ fontSize: 18 }} />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="View Source" arrow>
                                                <IconButton 
                                                    size="small"
                                                    href={item.link} 
                                                    target="_blank" 
                                                    sx={{ 
                                                        bgcolor: 'rgba(13, 53, 89, 0.05)', 
                                                        color: '#0d3559',
                                                        '&:hover': { bgcolor: '#0d3559', color: 'white' },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <ExternalLinkIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <Grid item xs={12}>
                                <Box sx={{ py: 10, textAlign: 'center', border: '1px dashed grey', borderRadius: 2 }}>
                                    <Typography color="textSecondary">No news items found matching your criteria.</Typography>
                                </Box>
                            </Grid>
                        )}
                    </Grid>

                    {filteredNews.length > rowsPerPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(e, val) => this.setState({ page: val })}
                                color="primary"
                                size="large"
                                shape="rounded"
                            />
                        </Box>
                    )}

                    {!isLoading && this.renderHelper()}
                </Box>

                {/* Preview/Edit Modal */}
                <Modal
                    open={isPreviewOpen}
                    onClose={() => this.setState({ isPreviewOpen: false })}
                >
                    <Box className={classes.modalContent}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h5" fontWeight="bold">
                                {isEditing ? 'Edit Resource Draft' : 'AI Generated Preview'}
                            </Typography>
                            <Box display="flex" gap={1} alignItems="center">
                                <Tooltip title={isEditing ? "Preview" : "Edit"} arrow>
                                    <IconButton
                                        onClick={() => this.setState({ isEditing: !isEditing })}
                                        sx={{ color: '#0d3559' }}
                                    >
                                        {isEditing ? <ViewIcon sx={{ fontSize: 20 }} /> : <EditIcon sx={{ fontSize: 20 }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={isPublishing ? "Saving..." : "Save as Draft"} arrow>
                                    <IconButton
                                        onClick={() => this.handlePublish(true)}
                                        disabled={isPublishing || this.state.isSharing || !permissions.canCreate}
                                        sx={{
                                            color: '#525F7F',
                                            '&:hover': {
                                                bgcolor: 'rgba(0,0,0,0.04)',
                                                color: '#0d3559'
                                            }
                                        }}
                                    >
                                        {isPublishing ? <CircularProgress size={18} color="inherit" /> : <SaveIcon sx={{ fontSize: 20 }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={isPublishing ? "Publishing..." : (editedDraft.category === 'newsletter' ? "Publish & Notify Subscribers" : "Publish to Website")} arrow>
                                    <IconButton
                                        onClick={() => this.handlePublish(false)}
                                        disabled={isPublishing || this.state.isSharing || !permissions.canCreate}
                                        sx={{
                                            color: '#2e7d32',
                                            '&:hover': {
                                                bgcolor: 'rgba(46, 125, 50, 0.08)'
                                            }
                                        }}
                                    >
                                        {isPublishing ? <CircularProgress size={18} color="inherit" /> : <PlayCircleOutlineIcon sx={{ fontSize: 22 }} />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel / Close" arrow>
                                    <IconButton onClick={() => this.setState({ isPreviewOpen: false })} sx={{ color: '#525F7F' }}>
                                        <CloseIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Base URL - only show if additional subdomain exists */}
                        {this.state.availableBaseUrls?.some(item => !item.isPrimary && item.url) && (
                            <Box sx={{ mb: 2.5, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#8898AA', fontWeight: 700 }}>
                                    BASE URL
                                </Typography>
                                <Select
                                    value={editedDraft.targetBaseUrl || this.state.targetBaseUrl || ""}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        this.setState({
                                            targetBaseUrl: val,
                                            editedDraft: { ...editedDraft, targetBaseUrl: val }
                                        });
                                    }}
                                    fullWidth
                                    displayEmpty
                                    size="small"
                                    variant="outlined"
                                    renderValue={(selected) => {
                                        const cleanSel = String(selected || "").trim().replace(/\/$/, "");
                                        const matched = this.state.availableBaseUrls.find(i => 
                                            (i.isPrimary && !cleanSel) || 
                                            (i.url?.replace(/\/$/, "") === cleanSel)
                                        );
                                        if (matched) {
                                            return (
                                                <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                                    {matched.isPrimary ? `Domain : ${matched.url}` : `Subdomain : ${matched.url}`}
                                                </span>
                                            );
                                        }
                                        return selected || "";
                                    }}
                                    sx={{
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-root': { borderRadius: '8px' },
                                        '& .MuiSelect-select': { py: 1.2, fontWeight: 600, color: '#32325D' }
                                    }}
                                >
                                    {this.state.availableBaseUrls.map((item) => (
                                        <MenuItem key={item.id || item.url} value={item.isPrimary ? "" : item.url}>
                                            <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                                                {item.isPrimary ? `Domain : ${item.url}` : `Subdomain : ${item.url}`}
                                            </span>
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" sx={{ color: '#8898AA', display: 'block', mt: 0.5, fontSize: '11px' }}>
                                    Select a Base URL to publish this content to.
                                </Typography>
                            </Box>
                        )}

                        {isEditing ? (
                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    label="Resource Title"
                                    fullWidth
                                    variant="outlined"
                                    className={classes.formField}
                                    value={editedDraft.title}
                                    onChange={(e) => {
                                        const newTitle = e.target.value;
                                        this.setState({ 
                                            editedDraft: { 
                                                ...editedDraft, 
                                                title: newTitle,
                                                slug: newTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                                            } 
                                        });
                                    }}
                                />

                                <Box className={classes.formField}>
                                    <Typography variant="subtitle2" gutterBottom>Thumbnail URL</Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            value={editedDraft.thumbnail}
                                            onChange={(e) => this.setState({ editedDraft: { ...editedDraft, thumbnail: e.target.value } })}
                                        />
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            startIcon={isUploading ? <CircularProgress size={20} /> : <UploadIcon />}
                                            disabled={isUploading || !permissions.canCreate}
                                        >
                                            Upload
                                            <input type="file" hidden accept="image/*" onChange={this.handleFileUpload} disabled={!permissions.canCreate} />
                                        </Button>
                                    </Box>
                                </Box>

                                <Typography variant="subtitle2" gutterBottom>Content</Typography>
                                <RichTextEditor
                                    value={editedDraft.content}
                                    onChange={(val) => this.setState({ editedDraft: { ...editedDraft, content: val } })}
                                    editorRef={this.editorRef}
                                />

                                <Grid container spacing={2} sx={{ mt: 2 }}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            label="Author Name"
                                            fullWidth
                                            variant="outlined"
                                            value={editedDraft.authorName || ''}
                                            onChange={(e) => this.setState({ editedDraft: { ...editedDraft, authorName: e.target.value } })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            label="Sort Order"
                                            fullWidth
                                            type="number"
                                            variant="outlined"
                                            value={editedDraft.sort}
                                            onChange={(e) => this.setState({ editedDraft: { ...editedDraft, sort: parseInt(e.target.value) || 0 } })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            label="Custom Slug"
                                            fullWidth
                                            variant="outlined"
                                            value={editedDraft.slug}
                                            onChange={(e) => this.setState({ editedDraft: { ...editedDraft, slug: e.target.value } })}
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 3, mb: 3 }}>
                                    <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#8898AA', fontWeight: 700 }}>
                                      SEO KEYWORDS
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                      {(editedDraft.metaKeywords || []).map((keyword, i) => (
                                        <Chip
                                          key={i}
                                          label={keyword}
                                          size="small"
                                          onDelete={() => {
                                            const newKeywords = [...editedDraft.metaKeywords];
                                            newKeywords.splice(i, 1);
                                            this.setState({
                                              editedDraft: { ...editedDraft, metaKeywords: newKeywords }
                                            });
                                          }}
                                          sx={{ bgcolor: 'rgba(13,53,89,0.08)', color: '#0d3559', fontWeight: 600 }}
                                        />
                                      ))}
                                    </Box>
                                    <TextField
                                      placeholder="Type keyword and press Enter"
                                      value={this.state.seoKeywordInput || ''}
                                      onChange={(e) => this.setState({ seoKeywordInput: e.target.value })}
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter' && (this.state.seoKeywordInput || '').trim()) {
                                          e.preventDefault();
                                          const keyword = this.state.seoKeywordInput.trim();
                                          if (!(editedDraft.metaKeywords || []).includes(keyword)) {
                                            this.setState({
                                              editedDraft: {
                                                ...editedDraft,
                                                metaKeywords: [...(editedDraft.metaKeywords || []), keyword]
                                              },
                                              seoKeywordInput: ''
                                            });
                                          } else {
                                            this.setState({ seoKeywordInput: '' });
                                          }
                                        }
                                      }}
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    />
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#8898AA', fontWeight: 700 }}>
                                      SEO SHORT DESCRIPTION
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter SEO meta description (recommended 150-160 characters)"
                                        multiline
                                        rows={3}
                                        variant="outlined"
                                        size="small"
                                        value={editedDraft.metaDescription || ''}
                                        onChange={(e) => this.setState({ editedDraft: { ...editedDraft, metaDescription: e.target.value } })}
                                        sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                        helperText={`${(editedDraft.metaDescription || '').length} / 160 characters`}
                                    />
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" sx={{ mb: 1, display: 'block', color: '#8898AA', fontWeight: 700 }}>SUBTITLE</Typography>
                                    <TextField
                                      fullWidth size="small" variant="outlined" placeholder="Enter subtitle..."
                                      value={editedDraft.subtitle || ''}
                                      onChange={(e) => this.setState({ editedDraft: { ...editedDraft, subtitle: e.target.value } })}
                                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    />
                                </Box>

                                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Tags (comma separated)</Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="e.g. Technology, AI, Future"
                                    value={editedDraft.tags.join(', ')}
                                    onChange={(e) => {
                                        const tags = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                                        if (tags.length > 3) {
                                            messageShow("Maximum 3 tags allowed!");
                                            // We allow the change so they can delete, but it will fail validation on publish
                                        }
                                        this.setState({ editedDraft: { ...editedDraft, tags } });
                                    }}
                                    error={editedDraft.tags.length > 3}
                                    helperText={editedDraft.tags.length > 3 ? "Maximum 3 tags allowed" : ""}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ mt: 2, maxHeight: '70vh', overflowY: 'auto', pr: 2 }}>
                                {editedDraft.thumbnail && (
                                    <Box sx={{ width: '100%', height: 350, mb: 4, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                                        <img 
                                            src={editedDraft.thumbnail} 
                                            alt="Hero" 
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                        />
                                    </Box>
                                )}
                                
                                <Box sx={{ mb: 4 }}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                        {editedDraft.tags.map((tag, i) => (
                                            <Chip 
                                                key={i} 
                                                label={tag.toUpperCase()} 
                                                size="small" 
                                                sx={{ 
                                                    fontWeight: 700, 
                                                    fontSize: '0.65rem',
                                                    color: '#0d3559',
                                                    bgcolor: 'rgba(13, 53, 89, 0.1)',
                                                    border: 'none'
                                                }} 
                                            />
                                        ))}
                                        {/* Removed chips from preview */}
                                    </Box>
                                    
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 700, 
                                        color: '#000000', 
                                        lineHeight: 1.3,
                                        mb: 1.5,
                                    }}>
                                        {editedDraft.title}
                                    </Typography>

                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 2, 
                                        pb: 2, 
                                        borderBottom: '1px solid #F1F3F9',
                                        color: '#8898AA'
                                    }}>
                                        {editedDraft.authorName && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <UserIcon sx={{ fontSize: 14 }} />
                                                <Typography variant="caption" fontWeight={600}>
                                                    {editedDraft.authorName}
                                                </Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <CalendarIcon sx={{ fontSize: 14 }} />
                                            <Typography variant="caption">{new Date().toLocaleDateString()}</Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    className="ql-editor ck-content"
                                    onClick={(e) => {
                                        const link = e.target.closest('a');
                                        if (link && link.href) {
                                            e.preventDefault();
                                            window.open(link.href, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    dangerouslySetInnerHTML={{ 
                                        __html: DOMPurify.sanitize(editedDraft.content, {
                                            ADD_TAGS: ["iframe", "figure", "oembed", "video", "source", "script"],
                                            ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src", "data-oembed-url", "url", "width", "height", "style", "title", "loading", "autoplay", "muted", "controls", "playsinline", "srcdoc", "itemprop", "itemtype", "content", "target", "rel"]
                                        }) 
                                    }}
                                    sx={{ 
                                        fontSize: '0.9rem', 
                                        lineHeight: 1.6,
                                        color: '#000000',
                                        overflowWrap: 'break-word',
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        '& h1, & h2, & h3, & h4, & h5, & h6': { 
                                            color: '#000000', 
                                            mt: 2, 
                                            mb: 1, 
                                            fontWeight: 700, 
                                            fontSize: '1.25rem !important' 
                                        },
                                        '& p': { mb: 1.5 },
                                        '& iframe': {
                                            width: '100% !important',
                                            aspectRatio: '16 / 9',
                                            minHeight: { xs: 200, sm: 300, md: 450 },
                                            borderRadius: '12px',
                                            border: 'none',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            my: 3,
                                            display: 'block',
                                            background: '#000'
                                        },
                                        '& figure.media, & figure, & oembed': {
                                            width: '100% !important',
                                            margin: '24px 0 !important',
                                            display: 'block'
                                        }
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Modal>


            </Box>
        );
    }
}

AiBlog.propTypes = {
    classes: PropTypes.object.isRequired,
    messageShow: PropTypes.func.isRequired,
    getAiNews: PropTypes.func.isRequired,
    generateBlogFromNews: PropTypes.func.isRequired,
    getPreferredTopics: PropTypes.func.isRequired,
    createEvent: PropTypes.func.isRequired,
    sendFbPost: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    getBaseUrls: PropTypes.func,
};

export default connect(null, {
    messageShow,
    getAiNews,
    getCms,
    generateBlogFromNews,
    createOrUpdateCms,
    uploadThumbnail,
    getPreferredTopics,
    createEvent,
    sendFbPost,
    getBaseUrls
})(withRouter(withStyles(styles)(AiBlog)));
